/**
 * Client card component for dashboard
 *
 * @author Claude
 */
import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ClientSummary } from '../../types/client';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import { format } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  Line,
} from 'recharts';

interface ClientCardProps {
  client?: ClientSummary;
  isAddCard?: boolean;
  onAddClick?: () => void;
  onEdit?: (client: ClientSummary) => void;
  onDelete?: (client: ClientSummary) => void;
  loading?: boolean;
  error?: string;
}

/**
 * Card component displaying client information or add new client button
 * @param {ClientCardProps} props - Component props
 * @returns {JSX.Element} Client card component
 *
 * @a11y Card has proper focus management and semantic structure
 * @a11y Interactive elements have accessible names
 * @a11y Chart has text alternative
 */
export const ClientCard = ({
  client,
  isAddCard,
  onAddClick,
  onEdit,
  onDelete,
  loading,
  error,
}: ClientCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Handle card click
  const handleCardClick = () => {
    if (isAddCard && onAddClick) {
      onAddClick();
    } else if (client) {
      navigate(`/clients/${client.id}`);
    }
  };

  // Handle add data click - prevent propagation to card click
  const handleAddDataClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (client) {
      navigate(`/clients/${client.id}/reports/new`);
    }
  };

  // If this is the "Add New Client" card
  if (isAddCard) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          minHeight: 220,
          bgcolor: 'background.default',
          border: `1px dashed ${theme.palette.grey[300]}`,
          '&:hover': {
            bgcolor: 'background.paper',
            borderColor: theme.palette.primary.main,
          },
        }}
        onClick={handleCardClick}
        component="article"
        aria-label="Add new client"
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
          }}
        >
          <AddIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="subtitle1" component="h3">
            Add New Client
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Regular client card
  if (!client) return null;

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
      onClick={handleCardClick}
      component="article"
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Typography variant="h6" component="h3" gutterBottom noWrap>
          {client.name}
        </Typography>

        {/* Client description */}
        {client.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {client.description}
          </Typography>
        )}

        {/* Last updated info */}
        <Typography
          variant="body2"
          color="text.secondary"
          component="div"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 1,
          }}
        >
          <span>Last updated: </span>
          <Chip
            label={
              client.latestReportDate
                ? format(new Date(client.latestReportDate), 'MMM d, yyyy')
                : 'No reports'
            }
            size="small"
            variant="outlined"
          />
        </Typography>
      </CardContent>

      {/* Maturity trend chart */}
      <Box
        sx={{ px: 2, pb: 1 }}
        aria-label={`Accessibility maturity trend chart for ${client.name}`}
      >
        <ResponsiveContainer width="100%" height={60}>
          <LineChart data={[]}>
            <XAxis dataKey="date" hide />
            <YAxis hide domain={[0, 100]} />
            <ChartTooltip />
            <Line type="monotone" dataKey="score" stroke={theme.palette.primary.main} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <CardActions sx={{ pt: 0 }}>
        <Button
          size="small"
          startIcon={<BarChartIcon />}
          onClick={e => {
            e.stopPropagation();
            navigate(`/clients/${client.id}`);
          }}
        >
          View Details
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Add new report data">
          <IconButton
            size="small"
            onClick={handleAddDataClick}
            aria-label="Add new report data"
            color="primary"
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};
